import { Dialog as BaseDialog } from "@base-ui/react/dialog"
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/20/solid"
import clsx from "clsx"
import { type ReactNode, useRef, useState } from "react"
import type { PropsWithChildren } from "react"

export function Dialog({
  open,
  title,
  back,
  children,
  header,
  onClose,
  onCloseAnimationEnd,
  preserveHeight = false,
  dismissible = true,
  bgClassName = "bg-white dark:bg-gray-800",
}: PropsWithChildren<{
  header?: ReactNode
  open: boolean
  title?: ReactNode
  back?: () => void
  onClose: () => void
  onCloseAnimationEnd?: () => void
  preserveHeight?: boolean
  dismissible?: boolean
  bgClassName?: string
}>) {
  const [isScrolled, setIsScrolled] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleScroll = () => {
    if (!scrollContainerRef.current) return
    setIsScrolled(scrollContainerRef.current.scrollTop > 0)
  }

  return (
    <BaseDialog.Root
      open={open}
      onOpenChange={(open, details) => {
        if (open) return
        if (!dismissible && details.reason !== "close-press") return
        onClose()
      }}
      onOpenChangeComplete={(open) => {
        if (!open) {
          setIsScrolled(false)
          onCloseAnimationEnd?.()
        }
      }}
    >
      <BaseDialog.Portal>
        <BaseDialog.Backdrop
          className={clsx(
            "fixed inset-0 z-10 bg-gray-900/80 transition-opacity duration-200 ease-out dark:bg-gray-900/90",

            "data-ending-style:opacity-0 data-starting-style:opacity-0 data-ending-style:duration-200 data-ending-style:ease-in"
          )}
        />

        <div className="fixed inset-0 z-20 w-screen">
          <div className="flex min-h-full items-end justify-center p-2 pb-safe-offset-2 text-center sm:items-center sm:p-0 has-sidebar:lg:pl-74 [@media(min-width:640px)_and_(min-height:850px)]:items-start [@media(min-width:640px)_and_(min-height:850px)]:pt-[10dvh]">
            <BaseDialog.Popup
              className={clsx(
                "dark:-outline-offset-1 relative flex max-h-[min(90dvh,700px)] w-full max-w-sm transform flex-col overflow-hidden rounded-3xl text-left shadow-xl transition-all duration-300 ease-out sm:my-8 dark:outline dark:outline-white/5",

                "data-starting-style:translate-y-4 data-starting-style:opacity-0 sm:data-starting-style:translate-y-0 sm:data-starting-style:scale-95",

                "data-ending-style:translate-y-4 data-ending-style:opacity-0 data-ending-style:duration-200 data-ending-style:ease-in sm:data-ending-style:translate-y-0 sm:data-ending-style:scale-95",

                preserveHeight && "h-screen-safe",

                bgClassName
              )}
            >
              <div
                className={clsx(
                  "space-y-2 border-b p-5 transition-colors",
                  header ? "pb-5" : "pb-2",
                  isScrolled
                    ? "border-gray-200 dark:border-white/10"
                    : "border-transparent"
                )}
              >
                <div
                  className={clsx(
                    "-mt-2.5 -mr-2.5 flex min-h-10 items-center justify-between",
                    back && "-ml-2.5"
                  )}
                >
                  {back && (
                    <button
                      type="button"
                      onClick={back}
                      className="flex size-10 items-center justify-center rounded-xl text-gray-600 hover:bg-gray-900/5 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                    >
                      <span className="sr-only">Back</span>
                      <ArrowLeftIcon className="size-5" />
                    </button>
                  )}

                  {title ? (
                    <BaseDialog.Title className="font-semibold text-base text-gray-900 dark:text-white">
                      {title}
                    </BaseDialog.Title>
                  ) : (
                    <div />
                  )}

                  <BaseDialog.Close className="flex size-10 items-center justify-center rounded-xl text-gray-600 hover:bg-gray-900/5 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white">
                    <XMarkIcon className="size-5" />
                  </BaseDialog.Close>
                </div>

                {header}
              </div>

              <div
                className={clsx("flex-1 overflow-y-auto px-5 pb-5")}
                ref={scrollContainerRef}
                onScroll={handleScroll}
              >
                {children}
              </div>
            </BaseDialog.Popup>
          </div>
        </div>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}
